import React, { useMemo, useState, Component } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, CopyIcon, CheckIcon, ClockIcon, ExternalLinkIcon, FileTextIcon, WebhookIcon, ListIcon, ReceiptIcon, ActivityIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { getEntryById, getRelatedEntries, getPayoutForEntry, merchantReason, type BalanceEntry, type BalanceTypeLabel, type ActivityType } from '../mocks/balanceData';
import { nowMs } from '../../../shared/lib/relativeDates';
const balanceTypeColors: Record<BalanceTypeLabel, string> = {
  Available: 'bg-green-500',
  Incoming: 'bg-amber-400',
  Reserved: 'bg-indigo-500',
  'On hold': 'bg-red-400'
};
const balanceTypeBadge: Record<BalanceTypeLabel, string> = {
  Available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Incoming: 'bg-amber-50 text-amber-700 border-amber-200',
  Reserved: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'On hold': 'bg-red-50 text-red-700 border-red-200'
};
function formatDateTimeLong(d: Date) {
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
function formatDateTimeWithSeconds(d: Date) {
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}
function activityDot(activityType: ActivityType, balanceType: BalanceTypeLabel) {
  if (activityType === 'Chargeback' || activityType === 'Chargeback fee') return 'bg-red-500';
  if (activityType === 'Refund' || activityType === 'Void') return 'bg-orange-400';
  if (activityType === 'Payout') return 'bg-gray-700';
  if (activityType === 'Review hold') return 'bg-red-400';
  if (activityType === 'Review release' || activityType === 'Chargeback reversal') return 'bg-emerald-500';
  return balanceTypeColors[balanceType];
}
// ────────────────────────────────────────────────────────────────────
// Section primitives
function Section({
  icon: Icon,
  title,
  description,
  children
}: {
  icon: ComponentType<{
    size?: number;
    className?: string;
  }>;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return <section className="bg-white border border-gray-200 rounded-lg" data-id="element-2877" data-mp="1wa55xklbz1ef">
      <div className="px-6 py-4 border-b border-gray-200" data-id="element-2878" data-mp="20aytgcuv2e6s">
        <div className="flex items-center gap-2" data-id="element-2879" data-mp="259a0kftrualb">
          <Icon size={15} className="text-gray-500" aria-hidden="true" data-id="element-2880" data-mp="257yweewez0to" />
          <h2 className="text-sm font-semibold text-gray-900" data-id="element-2881" data-mp="2a7tqerbhk6iy">{title}</h2>
        </div>
        {description && <p className="text-xs text-gray-500 mt-0.5 ml-[22px]" data-id="element-2882" data-mp="2f6eool76gg4r">
            {description}
          </p>}
      </div>
      {children}
    </section>;
}
function CopyButton({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  const [copied, setCopied] = useState(false);
  return <button type="button" onClick={async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  }} className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors" aria-label={`Copy ${label}`} data-id="element-2883" data-mp="2y3ycj7cj9hvt">
      {copied ? <CheckIcon size={14} className="text-emerald-600" aria-hidden="true" data-id="element-2884" data-mp="3h1rf28j8vpso" /> : <CopyIcon size={14} aria-hidden="true" data-id="element-2885" data-mp="3d1h38f8ru55w" />}
    </button>;
}
function DetailRow({
  label,
  children,
  copyable,
  to
}: {
  label: string;
  children: React.ReactNode;
  /** Value to copy to clipboard, when supported */
  copyable?: string;
  /** When set, wraps the value in a Link */
  to?: string;
}) {
  const valueNode = to && copyable ? <Link to={to} className="font-mono text-sm text-yellow-700 hover:text-yellow-800 underline-offset-2 hover:underline inline-flex items-center gap-1" data-id="element-2886" data-mp="1rncvs3qzwv4k">
        {children}
        <ExternalLinkIcon size={11} aria-hidden="true" data-id="element-2887" data-mp="3kbak6fw4m7ss" />
      </Link> : <span className="text-sm text-gray-900" data-id="element-2888" data-mp="36jw0s7yb44l7">{children}</span>;
  return <div className="grid grid-cols-3 gap-4 px-6 py-3 border-b border-gray-100 last:border-0 items-center" data-id="element-2889" data-mp="36ht10w7cqx3a">
      <dt className="col-span-1 text-xs text-gray-500" data-id="element-2890" data-mp="32xdlozkk02i">{label}</dt>
      <dd className="col-span-2 flex items-center justify-between gap-2 min-w-0" data-id="element-2891" data-mp="33f9gkibkc9w">
        <span className="min-w-0 truncate" data-id="element-2892" data-mp="737j70jg8ogu">{valueNode}</span>
        {copyable && <CopyButton value={copyable} label={label} data-id="element-2893" data-mp="3pan7t2utniwf" />}
      </dd>
    </div>;
}
// ────────────────────────────────────────────────────────────────────
export function BalanceActivityDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const entry = id ? getEntryById(id) : undefined;
  const related = entry ? getRelatedEntries(entry.reference) : [];
  const payoutEntry = entry ? getPayoutForEntry(entry) : undefined;
  const isCredit = entry ? entry.amount >= 0 : false;
  const formatAmt = (amt: number) => formatCurrencyAmount(Math.abs(amt), {
    fractionDigits: 2
  });
  const formatSigned = (amt: number) => `${amt >= 0 ? '+' : '−'}${formatAmt(amt)}`;
  // ── ID derivation ──────────────────────────────────────────────
  // The user wants "Transaction ID" exposed (instead of Charge ID) and
  // linked back to the Transactions/Payments view. The source ref for
  // anything born from a customer payment is ch_xxx — we treat that as the
  // Transaction ID for this detail page.
  const isPayoutEntry = entry?.activityType === 'Payout';
  const isProcessingFee = entry?.activityType === 'Processing fee';
  const transactionId = entry && entry.reference.startsWith('ch_') ? entry.reference : null;
  // Deterministically derive a Payout ID when a balance entry has a
  // scheduled payoutDate but the corresponding Payout balance row hasn't
  // been emitted yet (future payouts). Keeps the value stable across
  // renders so the merchant can copy + reconcile against the eventual
  // payout statement.
  const deriveScheduledPayoutId = (e: NonNullable<typeof entry>): string | null => {
    if (!e.payoutDate) return null;
    const dayKey = e.payoutDate.toISOString().slice(0, 10).replace(/-/g, '');
    const companyKey = (e.companyId || 'mst').slice(0, 4);
    let h = 0;
    for (let i = 0; i < (companyKey + dayKey).length; i++) {
      h = h * 31 + (companyKey + dayKey).charCodeAt(i) >>> 0;
    }
    return `po_${(h % 9000 + 1000).toString()}`;
  };
  const payoutId = !entry ? null : isPayoutEntry ? entry.reference : payoutEntry?.reference ?? deriveScheduledPayoutId(entry);
  // ── Payment breakdown ──────────────────────────────────────────
  // For a Payment entry we know: gross = entry.amount, processing fee ≈ 2.9%
  // + 0.30, scheme fee small, reserve = 20% of gross. For other entries we
  // surface a single line.
  const breakdown = useMemo(() => {
    if (!entry) return [];
    if (entry.activityType === 'Payment' && entry.amount > 0) {
      const gross = entry.amount;
      const interchange = Math.round(gross * 0.015 * 100) / 100;
      const schemeFee = Math.round(gross * 0.001 * 100) / 100;
      const processorFee = Math.round((gross * 0.014 + 0.3) * 100) / 100;
      const totalFees = Math.round((interchange + schemeFee + processorFee) * 100) / 100;
      const reserve = Math.round(gross * 0.2 * 100) / 100;
      const net = Math.round((gross - totalFees - reserve) * 100) / 100;
      return [{
        label: 'Gross amount',
        amount: gross,
        muted: false
      }, {
        label: 'Interchange fee',
        amount: -interchange,
        muted: true,
        sub: 'Card network'
      }, {
        label: 'Scheme fee',
        amount: -schemeFee,
        muted: true,
        sub: 'Card network'
      }, {
        label: 'Processing fee',
        amount: -processorFee,
        muted: true,
        sub: '1.4% + MYR 0.30'
      }, {
        label: 'Reserve hold',
        amount: -reserve,
        muted: true,
        sub: '20% held for 30 days'
      }, {
        label: 'Net to Available',
        amount: net,
        muted: false,
        emphasize: true
      }];
    }
    return [{
      label: entry.activityType,
      amount: entry.amount,
      muted: false
    }];
  }, [entry]);
  // ── Events (mock API event log) ────────────────────────────────
  const events = useMemo(() => {
    if (!entry) return [];
    const base = entry.date.getTime();
    return [{
      ts: new Date(base),
      type: 'payment_intent.created',
      message: 'Payment intent created via Checkout'
    }, {
      ts: new Date(base + 800),
      type: 'payment_intent.requires_action',
      message: '3DS authentication challenge issued'
    }, {
      ts: new Date(base + 4200),
      type: 'payment_intent.succeeded',
      message: `Authorized for ${formatAmt(Math.abs(entry.amount))}`
    }, {
      ts: new Date(base + 4400),
      type: 'charge.succeeded',
      message: `Charge ${entry.reference} captured`
    }];
  }, [entry]);
  // ── Logs (API request log) ─────────────────────────────────────
  // Surface the API calls the merchant's integration made that resulted in
  // this ledger entry, mirroring Stripe-style request logs.
  const logs = useMemo(() => {
    if (!entry) return [];
    const base = entry.date.getTime();
    type LogRow = {
      ts: Date;
      method: 'GET' | 'POST' | 'DELETE';
      path: string;
      status: number;
    };
    const isPayment = entry.activityType === 'Payment' || entry.reference.startsWith('ch_');
    const isPayout = entry.activityType === 'Payout';
    const isRefund = entry.activityType === 'Refund';
    const rows: LogRow[] = [];
    if (isPayment) {
      rows.push({
        ts: new Date(base - 1500),
        method: 'POST',
        path: '/v1/tokens',
        status: 200
      }, {
        ts: new Date(base - 800),
        method: 'POST',
        path: '/v1/payment_intents',
        status: 200
      }, {
        ts: new Date(base),
        method: 'POST',
        path: `/v1/charges`,
        status: 200
      }, {
        ts: new Date(base + 600),
        method: 'GET',
        path: `/v1/charges/${entry.reference}`,
        status: 200
      });
    } else if (isRefund) {
      rows.push({
        ts: new Date(base),
        method: 'POST',
        path: '/v1/refunds',
        status: 200
      }, {
        ts: new Date(base + 400),
        method: 'GET',
        path: `/v1/refunds/${entry.reference}`,
        status: 200
      });
    } else if (isPayout) {
      rows.push({
        ts: new Date(base),
        method: 'POST',
        path: '/v1/payouts',
        status: 200
      }, {
        ts: new Date(base + 500),
        method: 'GET',
        path: `/v1/payouts/${entry.reference}`,
        status: 200
      });
    } else {
      rows.push({
        ts: new Date(base),
        method: 'GET',
        path: `/v1/balance_transactions/${entry.id}`,
        status: 200
      });
    }
    return rows.sort((a, b) => b.ts.getTime() - a.ts.getTime());
  }, [entry]);
  // Early return AFTER all hooks have run so hook order stays stable
  // across renders regardless of whether the entry exists.
  if (!entry) {
    return <div className="max-w-4xl mx-auto py-16 text-center" data-id="element-2894" data-mp="1opwxo4eckd6t">
        <p className="text-gray-600 mb-4" data-id="element-2895" data-mp="1tqkmqkj0mjye">Balance activity not found.</p>
        <Link to="/balance-activity" className="text-sm font-medium text-yellow-700 hover:text-yellow-800" data-id="element-2896" data-mp="1yovy4zoyl2iq">
          ← Back to Balance activity
        </Link>
      </div>;
  }
  return <div className="max-w-5xl mx-auto pb-12" data-id="element-2897" data-mp="1425psntwe4qj">
      {/* Back link */}
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 rounded" data-id="element-2898" data-mp="13zt2rw9a946m">
        <ArrowLeftIcon size={14} aria-hidden="true" data-id="element-2899" data-mp="1rpdnajl349i0" />
        Balance activity
      </button>

      {/* Header — one piece of information per line, no duplicates. */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-id="element-2900" data-mp="21ocwc39ri75j">
        <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-2901" data-mp="1wprvq2qlllgk">
          <div className="min-w-0" data-id="element-2902" data-mp="7bd9bp31yao8">
            <div className="text-sm text-gray-500 mb-1" data-id="element-2903" data-mp="7alggm0uar1n">
              {entry.activityType}
            </div>
            <div className={`text-4xl font-semibold tracking-tight ${isCredit ? 'text-emerald-700' : 'text-gray-900'}`} data-id="element-2904" data-mp="2dliyzi22wm5">
              {isCredit ? '+' : '−'}
              {formatAmt(entry.amount)}
            </div>
            <p className="mt-2 text-sm text-gray-700" data-id="element-2905" data-mp="2j0jehaxgkgg0">
              {merchantReason(entry)}
            </p>
            <div className="flex items-center gap-2 mt-3 text-sm text-gray-500 flex-wrap" data-id="element-2906" data-mp="298x75po8r1ij">
              <span className="inline-flex items-center gap-1.5" data-id="element-2907" data-mp="296ki6sk2fqvz">
                <span className={`w-1.5 h-1.5 rounded-full ${balanceTypeColors[entry.balanceType]}`} aria-hidden="true" data-id="element-2908" data-mp="1v7kubn4dng0v" />
                {entry.balanceType}
              </span>
              <span aria-hidden="true" className="text-gray-300" data-id="element-2909" data-mp="1ldb1637m2dp3">
                ·
              </span>
              <span data-id="element-2910" data-mp="3fozi2axekqc4">{formatDateTimeLong(entry.date)}</span>
              {entry.status === 'Scheduled' && <>
                  <span aria-hidden="true" className="text-gray-300" data-id="element-2911" data-mp="3koc62z00g3ga">
                    ·
                  </span>
                  <span className="text-gray-700" data-id="element-2912" data-mp="1kkjdhe3y5dse">Scheduled</span>
                </>}
            </div>
          </div>
          <div className="flex items-center gap-2 font-mono text-xs text-gray-500" data-id="element-2913" data-mp="1fkn6d5zyd68m">
            <span data-id="element-2914" data-mp="1bjbesuxiqaxs">{entry.id}</span>
            <CopyButton value={entry.id} label="Balance transaction ID" data-id="element-2915" data-mp="16m1sho5fefe6" />
          </div>
        </div>
      </div>

      <div className="space-y-6" data-id="element-2916" data-mp="11ni5dkf3ixk8">
        {/* 1. Related activity — grouped into business events, not raw
            ledger rows. One real-world event (a settlement, a reserve
            release) emits multiple ledger entries because of double-entry
            bookkeeping; we collapse those back into a single human line. */}
        {(() => {
        // Group ledger rows by exact timestamp — entries sharing a date
        // belong to the same business event (e.g. Settlement at T+1
        // emits an Incoming debit, an Available credit and a Reserve
        // hold credit all at the same instant).
        const groups = (() => {
          const map = new Map<number, {
            date: Date;
            rows: typeof related;
            isFuture: boolean;
            containsCurrent: boolean;
          }>();
          for (const r of related) {
            const key = r.date.getTime();
            let g = map.get(key);
            if (!g) {
              g = {
                date: r.date,
                rows: [] as typeof related,
                isFuture: key > nowMs(),
                containsCurrent: false
              };
              map.set(key, g);
            }
            g.rows.push(r);
            if (r.id === entry.id) g.containsCurrent = true;
          }
          return Array.from(map.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
        })();
        if (groups.length <= 1) return null;
        // Pick the "headline" activity for a group based on which one
        // drives the user-facing narrative.
        const ACTIVITY_HEADLINE_PRIORITY: ActivityType[] = ['Payment', 'Settlement', 'Reserve release', 'Refund', 'Void', 'Chargeback', 'Chargeback reversal', 'Chargeback fee', 'Review hold', 'Review release', 'Reserve hold', 'Processing fee', 'Payout', 'Adjustment'];
        const getHeadline = (rows: typeof related): ActivityType => {
          for (const a of ACTIVITY_HEADLINE_PRIORITY) {
            if (rows.some(r => r.activityType === a)) return a;
          }
          return rows[0].activityType;
        };
        const describe = (g: (typeof groups)[number]) => {
          const headline = getHeadline(g.rows);
          const future = g.isFuture;
          const amt = (n: number) => formatAmt(Math.abs(n));
          switch (headline) {
            case 'Payment':
              {
                const p = g.rows.find(r => r.activityType === 'Payment')!;
                return {
                  title: future ? 'Payment expected' : 'Payment received',
                  detail: `${amt(p.amount)} ${future ? 'will land in' : 'landed in'} Incoming`
                };
              }
            case 'Settlement':
              {
                const avail = g.rows.find(r => r.activityType === 'Settlement' && r.balanceType === 'Available');
                const reserve = g.rows.find(r => r.activityType === 'Reserve hold');
                const parts: string[] = [];
                if (avail) parts.push(`${amt(avail.amount)} to Available`);
                if (reserve) parts.push(`${amt(reserve.amount)} held in reserve`);
                return {
                  title: future ? 'Funds settling' : 'Funds settled',
                  detail: parts.join(' · ')
                };
              }
            case 'Reserve release':
              {
                const avail = g.rows.find(r => r.balanceType === 'Available');
                return {
                  title: future ? 'Reserve releasing' : 'Reserve released',
                  detail: avail ? `${amt(avail.amount)} moved from Reserved to Available` : ''
                };
              }
            case 'Reserve hold':
              {
                const hold = g.rows.find(r => r.activityType === 'Reserve hold')!;
                return {
                  title: future ? 'Reserve will be held' : 'Reserve held',
                  detail: `${amt(hold.amount)} moved to Reserved`
                };
              }
            case 'Refund':
              {
                const r = g.rows.find(x => x.activityType === 'Refund')!;
                return {
                  title: future ? 'Refund pending' : 'Refund issued',
                  detail: `${amt(r.amount)} deducted from Available`
                };
              }
            case 'Void':
              {
                const v = g.rows.find(x => x.activityType === 'Void')!;
                return {
                  title: 'Payment voided',
                  detail: `${amt(v.amount)} returned before settlement`
                };
              }
            case 'Chargeback':
              {
                const cb = g.rows.find(x => x.activityType === 'Chargeback');
                const fee = g.rows.find(x => x.activityType === 'Chargeback fee');
                const parts: string[] = [];
                if (cb) parts.push(`${amt(cb.amount)} disputed amount`);
                if (fee) parts.push(`${amt(fee.amount)} fee`);
                return {
                  title: 'Chargeback received',
                  detail: parts.join(' · ') + ' deducted from Available'
                };
              }
            case 'Chargeback reversal':
              {
                const rev = g.rows.find(x => x.activityType === 'Chargeback reversal')!;
                return {
                  title: 'Dispute won',
                  detail: `${amt(rev.amount)} returned to Available`
                };
              }
            case 'Review hold':
              {
                const onHold = g.rows.find(r => r.activityType === 'Review hold' && r.balanceType === 'On hold');
                return {
                  title: future ? 'Will be held for review' : 'Held for review',
                  detail: onHold ? `${amt(onHold.amount)} moved from Available to On hold` : ''
                };
              }
            case 'Review release':
              {
                const rel = g.rows.find(r => r.activityType === 'Review release');
                return {
                  title: future ? 'Will release from review' : 'Released from review',
                  detail: rel ? `${amt(rel.amount)} returned to Available` : ''
                };
              }
            case 'Processing fee':
              {
                const fee = g.rows.find(r => r.activityType === 'Processing fee')!;
                return {
                  title: future ? 'Processing fee scheduled' : 'Processing fee charged',
                  detail: `${amt(fee.amount)} deducted from Available`
                };
              }
            case 'Payout':
              {
                const p = g.rows.find(r => r.activityType === 'Payout')!;
                return {
                  title: future ? 'Payout scheduled' : 'Payout sent',
                  detail: `${amt(p.amount)} ${future ? 'will be sent to' : 'sent to'} your bank`
                };
              }
            case 'Adjustment':
              {
                const a = g.rows.find(r => r.activityType === 'Adjustment')!;
                const credit = a.amount >= 0;
                return {
                  title: future ? 'Adjustment scheduled' : 'Adjustment applied',
                  detail: `${amt(a.amount)} ${credit ? 'added to' : 'deducted from'} Available`
                };
              }
            default:
              return {
                title: headline,
                detail: ''
              };
          }
        };
        return <Section icon={ActivityIcon} title="Related activity" description="The lifecycle of this payment" data-id="element-2917" data-mp="3qv6x77trvdie">
              <ol className="relative px-6 py-4" data-id="element-2918" data-mp="3hxwl5k94rkcf">
                <span className="absolute left-9 top-6 bottom-6 w-px bg-gray-200" aria-hidden="true" data-id="element-2919" data-mp="3ly71nqujtt5j" />
                {groups.map((g, i) => {
              const {
                title,
                detail
              } = describe(g);
              const headline = getHeadline(g.rows);
              // Pick a primary balance type for dot colouring — prefer
              // the credit row (where money landed) for that event.
              const primaryRow = g.rows.find(r => r.amount >= 0) ?? g.rows[0];
              const linkRow = g.rows.find(r => r.id === entry.id) ?? primaryRow;
              return <li key={i} className="relative pl-9 py-3" data-id="element-2920" data-mp="3h7utig8ddhqq" data-mpi={i}>
                      <span className={`absolute left-2 top-4 w-3 h-3 rounded-full ring-4 ring-white ${activityDot(headline, primaryRow.balanceType)} ${g.isFuture ? 'opacity-60' : ''}`} aria-hidden="true" data-id="element-2921" data-mp="3v0kkz06o3ev8" />
                      <div className="min-w-0" data-id="element-2922" data-mp="3vu90e57d4ud">
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-2923" data-mp="3sy8ci1z0228">
                          <Link to={`/balance-activity/${linkRow.id}`} className={`text-sm font-medium ${g.containsCurrent ? 'text-gray-900' : 'text-gray-700 hover:text-yellow-700'}`} data-id="element-2924" data-mp="nwne9t772dn4">
                            {title}
                          </Link>
                          {g.containsCurrent && <span className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 px-1.5 py-0.5 rounded" data-id="element-2925" data-mp="4zm8451wm7hl">
                              This event
                            </span>}
                          {g.isFuture && !g.containsCurrent && <span className="text-xs text-gray-500 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded" data-id="element-2926" data-mp="izdtiqur2q5m">
                              Scheduled
                            </span>}
                        </div>
                        {detail && <div className="text-xs text-gray-600 mt-0.5" data-id="element-2927" data-mp="3etp7aezsiyuz">
                            {detail}
                          </div>}
                        <div className="text-xs text-gray-400 mt-0.5" data-id="element-2928" data-mp="354p0i9f1qz68">
                          {formatDateTimeLong(g.date)}
                        </div>
                      </div>
                    </li>;
            })}
              </ol>
            </Section>;
      })()}

        {/* 2. Details */}
        <Section icon={FileTextIcon} title="Details" data-id="element-2929" data-mp="2r3us3qs00y7k">
          <dl data-id="element-2930" data-mp="2m62z0tfmb61s">
            <DetailRow label="Balance transaction ID" copyable={entry.id} data-id="element-2931" data-mp="2m5jjfjdfwtqx">
              <span className="font-mono" data-id="element-2932" data-mp="2h7hzyxc3qsex">{entry.id}</span>
            </DetailRow>
            {transactionId && <DetailRow label="Transaction ID" copyable={transactionId} to={`/payment-requests?txn=${encodeURIComponent(transactionId)}`} data-id="element-2933" data-mp="kfhtlqadbg1a">
                {transactionId}
              </DetailRow>}
            {!transactionId && <DetailRow label="Source reference" copyable={entry.reference} data-id="element-2934" data-mp="2en3h6s7t1mkj">
                <span className="font-mono" data-id="element-2935" data-mp="1zy7e79kluzqs">{entry.reference}</span>
              </DetailRow>}
            <DetailRow label="Available on" data-id="element-2936" data-mp="24whbbppw4af1">
              {entry.balanceType === 'On hold' ? 'Under review' : entry.availableOn ? formatDateTimeLong(entry.availableOn) : formatDateTimeLong(entry.date)}
            </DetailRow>
            {/* The date the payout leaves us. We can't know when it actually
             lands in the destination bank account, so this is never framed as
             an arrival. */}
            {(payoutEntry || entry.payoutDate) && <DetailRow label="Payout date" data-id="element-2937" data-mp="1856mivz3ybog">
                {formatDateTimeLong(payoutEntry ? payoutEntry.date : entry.payoutDate!)}
              </DetailRow>}
            {payoutId && <DetailRow label="Payout ID" copyable={payoutId} to={`/payouts/${encodeURIComponent(payoutId)}`} data-id="element-2938" data-mp="zpr72jkgyfhj">
                {payoutId}
              </DetailRow>}
          </dl>
        </Section>
      </div>
    </div>;
}